/**
 * ContextEntryTree — status-grouped, expandable view of what is in (and what has
 * fallen out of) the parent agent's context window.
 *
 * Groups: In context (live) · Superseded (tombstoned by dedupe) · Compacted
 * (folded into a compaction summary). Collapsed to count + aggregate-token
 * headers by default; each group expands to per-entry identity rows. Identity
 * only — no content is ever shipped to the panel. The tree
 * reflects the CURRENT turn's window (the dedupe cache is per-turn); it is not a
 * session-long ledger.
 */

import { useState } from 'react';
import type { InContextEntry, SupersededEntry } from '../../types/context-types';

interface ContextEntryTreeProps {
  entries: InContextEntry[];
  supersededEntries: SupersededEntry[];
  compactedEntries: InContextEntry[];
}

function formatTokens(tokens: number): string {
  if (tokens >= 1000) return `${(tokens / 1000).toFixed(1)}K`;
  return String(tokens);
}

/** Human byte size for the envelope badge (e.g. 320000 → "320 KB"). */
function formatBytes(bytes: number): string {
  if (bytes >= 1_000_000) return `${(bytes / 1_000_000).toFixed(1)} MB`;
  if (bytes >= 1000) return `${Math.round(bytes / 1000)} KB`;
  return `${bytes} B`;
}

/** path ?? url ?? query ?? tool_name — same precedence as the old cached-sources
 * label (SessionContextPanel entry label). */
function entryLabel(toolName: string, sourceArgs: Record<string, unknown>): string {
  return (
    (sourceArgs.path as string) ??
    (sourceArgs.url as string) ??
    (sourceArgs.query as string) ??
    (sourceArgs.pattern as string) ??
    toolName
  );
}

function sumTokens(items: Array<{ estimated_tokens: number }>): number {
  return items.reduce((acc, e) => acc + (e.estimated_tokens ?? 0), 0);
}

interface GroupProps {
  status: 'in_context' | 'superseded' | 'compacted';
  title: string;
  count: number;
  /** Right-hand summary on the header, e.g. "45.3K" or "6.3K reclaimed". */
  summary: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}

function Group({ status, title, count, summary, defaultOpen, children }: GroupProps) {
  const [open, setOpen] = useState(!!defaultOpen);
  if (count === 0) return null;
  return (
    <div data-testid={`context-tree-group-${status}`} data-count={count}>
      <button
        type="button"
        className="w-full flex items-center justify-between text-micro text-muted-foreground py-0.5 hover:text-foreground"
        onClick={() => setOpen((o) => !o)}
      >
        <span className="flex items-center gap-1">
          <span className="inline-block w-2 text-center">{open ? '▾' : '▸'}</span>
          <span className="font-medium">{title}</span>
          <span>— {count} {count === 1 ? 'item' : 'items'} · {summary}</span>
        </span>
      </button>
      {open && <div className="ml-3 space-y-0.5">{children}</div>}
    </div>
  );
}

export function ContextEntryTree({ entries, supersededEntries, compactedEntries }: ContextEntryTreeProps) {
  const hasAny = entries.length > 0 || supersededEntries.length > 0 || compactedEntries.length > 0;
  if (!hasAny) {
    return (
      <p data-testid="context-entry-tree" className="text-micro text-muted-foreground">
        No tracked context entries this turn.
      </p>
    );
  }

  return (
    <div data-testid="context-entry-tree" className="space-y-0.5">
      <Group
        status="in_context"
        title="In context"
        count={entries.length}
        summary={formatTokens(sumTokens(entries))}
        defaultOpen
      >
        {entries.map((e, i) => (
          <div key={`ic-${i}`} className="flex items-center justify-between text-micro gap-2">
            <span className="flex items-center gap-1.5 min-w-0">
              <span className="text-muted-foreground shrink-0">{e.tool_name}</span>
              <span className="truncate" title={entryLabel(e.tool_name, e.source_args)}>
                {entryLabel(e.tool_name, e.source_args)}
              </span>
              {e.enveloped && (
                <span
                  data-testid="envelope-badge"
                  className="shrink-0 rounded-sm bg-muted px-1 text-micro text-muted-foreground"
                  title="Oversized result replaced with an envelope; full payload retained in the store and retrievable by handle"
                >
                  enveloped{typeof e.payload_bytes === 'number' ? ` · ${formatBytes(e.payload_bytes)}` : ''}
                </span>
              )}
            </span>
            <span className="text-muted-foreground whitespace-nowrap shrink-0">
              {formatTokens(e.estimated_tokens)}
            </span>
          </div>
        ))}
      </Group>

      <Group
        status="superseded"
        title="Superseded"
        count={supersededEntries.length}
        summary={`${formatTokens(sumTokens(supersededEntries))} reclaimed`}
      >
        {supersededEntries.map((e, i) => (
          <div key={`sup-${i}`} className="flex items-center justify-between text-micro gap-2">
            <span className="flex items-center gap-1.5 min-w-0">
              <span className="text-muted-foreground shrink-0">{e.tool_name}</span>
              <span className="truncate" title={entryLabel(e.tool_name, e.source_args)}>
                {entryLabel(e.tool_name, e.source_args)}
              </span>
              <span className="text-muted-foreground/70 shrink-0 whitespace-nowrap">
                superseded by newer read (iter {e.superseded_at_iteration})
              </span>
            </span>
            <span className="text-muted-foreground whitespace-nowrap shrink-0">
              {formatTokens(e.estimated_tokens)}
            </span>
          </div>
        ))}
      </Group>

      <Group
        status="compacted"
        title="Compacted"
        count={compactedEntries.length}
        summary="folded into summary"
      >
        {compactedEntries.map((e, i) => (
          <div key={`cmp-${i}`} className="flex items-center justify-between text-micro gap-2">
            <span className="flex items-center gap-1.5 min-w-0">
              <span className="text-muted-foreground shrink-0">{e.tool_name}</span>
              <span className="truncate" title={entryLabel(e.tool_name, e.source_args)}>
                {entryLabel(e.tool_name, e.source_args)}
              </span>
            </span>
            <span className="text-muted-foreground whitespace-nowrap shrink-0">
              {e.estimated_tokens > 0 ? formatTokens(e.estimated_tokens) : ''}
            </span>
          </div>
        ))}
      </Group>
    </div>
  );
}
